import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const MonthlyOverview = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [monthlyData, setMonthlyData] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);

  const fetchMonthlyData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/time-entries/monthly`, {
        headers: getAuthHeader()
      });
      setMonthlyData(response.data);
      const total = response.data.reduce((sum, day) => sum + day.hours, 0);
      setTotalHours(total);
    } catch (error) {
      console.error('Failed to fetch monthly data:', error);
    }
  }, [getAuthHeader]);

  const fetchCompletedTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tasks`, {
        headers: getAuthHeader()
      });
      const completedThisMonth = response.data.filter(task => {
        if (task.status !== 'done') return false;
        if (task.assigned_to !== user?.id) return false;
        return true;
      });
      setCompletedTasks(completedThisMonth.length);
    } catch (error) {
      console.error('Failed to fetch completed tasks:', error);
    }
  }, [getAuthHeader, user?.id]);

  useEffect(() => {
    fetchMonthlyData();
    fetchCompletedTasks();
  }, [fetchMonthlyData, fetchCompletedTasks]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'd', { locale: getLocale() });
  };

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const monthStart = startOfMonth(new Date());
  const monthEnd = endOfMonth(new Date());
  const daysWithHours = monthlyData.filter(d => d.hours > 0).length;
  const avgPerDay = daysWithHours > 0 ? (totalHours / daysWithHours).toFixed(1) : '0.0';

  return (
      <div data-testid="monthly-page">
        <h2 className="text-lg font-bold text-zinc-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('monthly.title')}
        </h2>
        <p className="text-zinc-600 mb-8">
          {format(monthStart, 'd. MMM', { locale: getLocale() })} - {format(monthEnd, 'd. MMM yyyy', { locale: getLocale() })}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-2xl border border-zinc-200"
            data-testid="total-hours-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#047857]" />
              </div>
              <div>
                <div className="text-sm text-zinc-600">{t('monthly.totalMonth')}</div>
                <div className="text-3xl font-bold text-zinc-900">{totalHours.toFixed(1)}h</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white p-6 rounded-2xl border border-zinc-200"
            data-testid="avg-hours-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#B45309]" />
              </div>
              <div>
                <div className="text-sm text-zinc-600">{t('monthly.avgPerDay')}</div>
                <div className="text-3xl font-bold text-zinc-900">{avgPerDay}h</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-2xl border border-zinc-200"
            data-testid="completed-tasks-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F3E8FF] rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#7E22CE]" />
              </div>
              <div>
                <div className="text-sm text-zinc-600">{t('monthly.completedTasks')}</div>
                <div className="text-3xl font-bold text-zinc-900">{completedTasks}</div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 sm:p-8 rounded-2xl border border-zinc-200"
          data-testid="chart-card"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('monthly.hoursPerDay')}
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#71717A"
                style={{ fontSize: '12px' }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#71717A"
                style={{ fontSize: '14px' }}
                label={{ value: t('monthly.hours'), angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E4E4E7',
                  borderRadius: '12px',
                  padding: '12px'
                }}
                formatter={(value) => [`${value} ${t('monthly.hours')}`, t('monthly.workTime')]}
                labelFormatter={(label) => format(new Date(label), 'd. MMMM yyyy', { locale: getLocale() })}
              />
              <Bar
                dataKey="hours"
                fill="#D93025"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <div className="mt-8 bg-white p-6 rounded-2xl border border-zinc-200">
          <h3 className="font-medium text-zinc-900 mb-2">{t('monthly.info')}</h3>
          <p className="text-sm text-zinc-600">
            {t('monthly.infoText')}
          </p>
        </div>
      </div>
  );
};

export default MonthlyOverview;
